import {
  deleteRecord,
  listRecords,
  saveRecord,
} from '@/services/localDb';
import { clearDeviceKey } from '@/services/device/deviceCredential';

export const DEVICE_ID_STORAGE_KEY = 'ewu_tpv_device_id';
export const LEGACY_DEVICE_ID_STORAGE_KEYS = ['ewu_device_id', 'device_id'];

let identityPromise = null;
let cachedDeviceId = null;
let identityPersisted = false;

export function isValidDeviceId(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || ''));
}

function randomHex(length) {
  const bytes = new Uint8Array(Math.ceil(length / 2));
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('').slice(0, length);
}

function createDeviceId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${randomHex(8)}-${randomHex(4)}-4${randomHex(3)}-${['8', '9', 'a', 'b'][Math.floor(Math.random() * 4)]}${randomHex(3)}-${randomHex(12)}`;
}

function readStorage(key) {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem(key);
  } catch (error) {
    console.warn('Unable to read EWU device identity:', error);
    return null;
  }
}

function writeStorage(deviceId) {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
  } catch (error) {
    console.warn('Unable to persist EWU device identity:', error);
  }
}

function readCompatibleStorageIdentity() {
  const current = readStorage(DEVICE_ID_STORAGE_KEY);
  if (isValidDeviceId(current)) return current;

  for (const key of LEGACY_DEVICE_ID_STORAGE_KEYS) {
    const legacy = readStorage(key);
    if (isValidDeviceId(legacy)) {
      writeStorage(legacy);
      return legacy;
    }
  }
  return null;
}

async function persistIndexedDbIdentity(deviceId) {
  const records = await listRecords('devices');
  const existing = records.find((record) => record.id === deviceId);
  const now = new Date().toISOString();
  await saveRecord('devices', {
    ...(existing || {}),
    id: deviceId,
    restaurant_id: existing?.restaurant_id || null,
    device_name: existing?.device_name || '',
    device_type: existing?.device_type || 'unknown',
    first_seen_at: existing?.first_seen_at || now,
    last_seen_at: existing?.last_seen_at || now,
    updated_at: now,
  });
}

export function getOrCreateDeviceIdSync() {
  const existing = readCompatibleStorageIdentity();
  if (existing) return existing;
  const deviceId = createDeviceId();
  writeStorage(deviceId);
  return deviceId;
}

export async function getDeviceId() {
  if (isValidDeviceId(cachedDeviceId)) return cachedDeviceId;
  const storageIdentity = readCompatibleStorageIdentity();
  if (storageIdentity) {
    cachedDeviceId = storageIdentity;
    return storageIdentity;
  }

  const records = await listRecords('devices');
  const indexedDbIdentity = records.find((record) => isValidDeviceId(record?.id))?.id || null;
  if (indexedDbIdentity) {
    cachedDeviceId = indexedDbIdentity;
    writeStorage(indexedDbIdentity);
  }
  return indexedDbIdentity;
}

export async function getOrCreateDeviceId() {
  if (isValidDeviceId(cachedDeviceId) && identityPersisted) {
    return cachedDeviceId;
  }
  if (!identityPromise) {
    identityPromise = (async () => {
      const existing = await getDeviceId();
      const deviceId = existing || getOrCreateDeviceIdSync();
      cachedDeviceId = deviceId;
      if (!identityPersisted) {
        await persistIndexedDbIdentity(deviceId);
        identityPersisted = true;
      }
      return deviceId;
    })().finally(() => {
      identityPromise = null;
    });
  }
  return identityPromise;
}

export async function resetDeviceIdentity() {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('ewu-device-identity-reset'));
  const current = await getDeviceId();
  if (current) {
    await clearDeviceKey(current);
    await deleteRecord('devices', current);
  }
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(DEVICE_ID_STORAGE_KEY);
      LEGACY_DEVICE_ID_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
    }
  } finally {
    identityPromise = null;
    cachedDeviceId = null;
    identityPersisted = false;
  }
}
